/**
 * Patient Registration Flow Hook
 *
 * This module provides the main state management hook for the patient
 * registration workflow. Implements comprehensive state management with
 * step submission handlers, API integration, and authentication-based
 * wizard step determination.
 *
 * @module hooks/usePatientRegistrationFlow
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type {
  PatientRegistrationState,
  PatientRegistrationData,
  PatientRegistrationFormData,
  PatientProfileFormData,
  PatientProfileApiRequest
} from '@/types/patientRegistration';
import { PatientApiError } from '@/types/patientRegistration';
import { usePatientValidation } from './usePatientValidation';
import { apiService } from '@/services/ApiService';
import { useAuth } from '@/contexts/AuthContext';
import { FileUtils } from '@/utils/fileUtils';
import { getLogger } from '@/services/shared/LoggerFactory';
import { referralStorage } from '@/utils/referralStorage';

/**
 * Hook return type
 */
export interface UsePatientRegistrationFlowReturn {
  state: PatientRegistrationState;
  actions: {
    setCurrentStep: (step: number) => void;
    updateFormData: (data: Partial<PatientRegistrationData>) => void;
    submitRegistration: (formData: PatientRegistrationFormData) => Promise<void>;
    submitProfile: (formData: PatientProfileFormData) => Promise<void>;
    resetFlow: () => void;
    setError: (field: string, message: string) => void;
    clearErrors: () => void;
    setLoading: (loading: boolean) => void;
  };
  validation: {
    validateStep: (step: number) => boolean;
    getStepErrors: (step: number) => string[];
    canProceedToStep: (step: number) => boolean;
    isStepCompleted: (step: number) => boolean;
    isFormValid: boolean;
  };
  computed: {
    progressPercentage: number;
  };
}

/**
 * Main patient registration flow hook
 */
export const usePatientRegistrationFlow = (): UsePatientRegistrationFlowReturn => {
  const logger = getLogger();
  const navigate = useNavigate();

  // State management
  const [registrationState, setRegistrationState] = useState<PatientRegistrationState>({
    currentStep: 1,
    formData: {},
    isLoading: false,
    errors: {},
    isSubmitting: false,
    completedSteps: new Set()
  });

  // Validation hook
  const validation = usePatientValidation();

  // Auth hook
  const { signIn, isAuthenticated } = useAuth();

  // No need for API service instance - using unified apiService


  // Determine wizard step based on auth/profile status and check for referral data
  useEffect(() => {
    const initializeWizardStep = async () => {
      try {
        // Determine the appropriate step based on authentication status
        if (!isAuthenticated) {
          // Check for referral data in session storage and pre-fill form data
          try {
            const storedReferralData = referralStorage.retrieve();
            logger.log('storedReferralData: ', storedReferralData);
            if (storedReferralData?.workflow?.finalReferral?.registration_prefill_data) {
              const prefillData = storedReferralData.workflow.finalReferral.registration_prefill_data;

              // Pre-fill form data from referral
              const referralFormData: Partial<PatientRegistrationData> = {};

              if (prefillData.patient_name) {
                referralFormData.firstName = prefillData.patient_name.split(' ')[0];
              }
              if (prefillData.patient_name) {
                referralFormData.lastName = prefillData.patient_name.split(' ').slice(1).join(' ');
              }
              if (prefillData.email) {
                referralFormData.email = prefillData.email;
              }
              if (prefillData.date_of_birth) {
                referralFormData.date_of_birth = prefillData.date_of_birth;
              }
              if (prefillData.phone && typeof prefillData.phone === 'string') {
                // Parse phone number to extract dial code and number
                const parsePhoneNumber = (phone: string) => {
                  // Remove any spaces, dashes, or parentheses
                  const cleanPhone = phone.replace(/[\s\-()]/g, '');

                  // Check for common dial codes
                  const dialCodes = [
                    { code: '+61', length: 3 }, // Australia
                    { code: '+1', length: 2 },  // US/Canada
                    { code: '+44', length: 3 }, // UK
                    { code: '+33', length: 3 }, // France
                    { code: '+49', length: 3 }, // Germany
                    { code: '+81', length: 3 }, // Japan
                    { code: '+86', length: 3 }, // China
                    { code: '+91', length: 3 }, // India
                  ];

                  // Check if phone starts with a dial code
                  for (const { code, length } of dialCodes) {
                    if (cleanPhone.startsWith(code)) {
                      return {
                        country_code: code,
                        number: cleanPhone.substring(length)
                      };
                    }
                  }

                  // If no dial code found, return original number with default +61
                  return {
                    country_code: '+61',
                    number: cleanPhone.startsWith('+') ? cleanPhone.substring(1) : cleanPhone
                  };
                };

                const { country_code, number } = parsePhoneNumber(prefillData.phone);

                referralFormData.phone = {
                  number,
                  country_code,
                  iso_code: 'AU' // Default to AU for now, could be enhanced to map from country_code
                };
              }

              // Update form data with prefilled values
              if (Object.keys(referralFormData).length > 0) {
                setRegistrationState(prevState => ({
                  ...prevState,
                  formData: {
                    ...prevState.formData,
                    ...referralFormData
                  }
                }));
                logger.log('Pre-filled registration form with referral data:', referralFormData);
              }
            }
          } catch (error) {
            logger.warn('Failed to load referral data for pre-filling:', error);
          }

          // User is not authenticated - set wizard step to 1
          setRegistrationState(prevState => ({
            ...prevState,
            currentStep: 1
          }));
          return;
        }

        // User is authenticated - check if profile exists
        try {
          await apiService.patient.getProfile();
          // Profile exists - redirect to dashboard
          navigate('/dashboard');
          return;
        } catch (error) {
          // Check if it's a 404 error (no profile)
          if (error instanceof PatientApiError && error.status === 404) {
            // User is authenticated but has no profile - set wizard step to 2
            setRegistrationState(prevState => ({
              ...prevState,
              currentStep: 2
            }));
            setCurrentStep(2);
          } else {
            // Other error - default to step 2 (profile creation)
            setRegistrationState(prevState => ({
              ...prevState,
              currentStep: 2
            }));
            setCurrentStep(2);
          }
        }
      } catch (error) {
        logger.warn('Failed to initialize wizard step:', error);
        // Default to step 1 on error
        setRegistrationState(prevState => ({
          ...prevState,
          currentStep: 1
        }));
        setCurrentStep(1);
      }
    };

    initializeWizardStep();
  }, [isAuthenticated]);


  // Actions
  const setCurrentStep = useCallback((step: number): void => {
    if (step >= 1 && step <= 2) {
      setRegistrationState(prevState => ({
        ...prevState,
        currentStep: step as 1 | 2,
        errors: {} // Clear errors when changing steps
      }));
    }
  }, []);

  const updateFormData = useCallback((data: Partial<PatientRegistrationData>): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        ...data
      }
    }));
  }, []);

  const setError = useCallback((field: string, message: string): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      errors: {
        ...prevState.errors,
        [field]: message
      }
    }));
  }, []);

  const clearErrors = useCallback((): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      errors: {}
    }));
  }, []);

  const setLoading = useCallback((loading: boolean): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      isLoading: loading
    }));
  }, []);

  const setSubmitting = useCallback((submitting: boolean): void => {
    setRegistrationState(prevState => ({
      ...prevState,
      isSubmitting: submitting
    }));
  }, []);

  // Step 1: Registration submission
  const submitRegistration = useCallback(async (formData: PatientRegistrationFormData): Promise<void> => {
    setSubmitting(true);
    clearErrors();

    try {
      // Validate form data
      const validationResult = validation.validateStep(1, formData);
      if (!validationResult.isValid) {
        setError('form', validationResult.error || 'Validation failed');
        return;
      }

      // Submit to API - using unified apiService for patient registration
      const apiRequest = {
        email: formData.email.trim(),
        password: formData.password,
        confirmPassword: formData.password, // Use same password for confirmation
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: 'patient' as const
      };

      // Make actual API call using PatientDomain
      const result = await apiService.patient.signUp({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      });

      // Log in after successful registration
      await signIn({
        email: apiRequest.email,
        password: apiRequest.password
      });

      // Update state with success
      updateFormData({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName
      });

      setRegistrationState(prevState => ({
        ...prevState,
        registrationId: result.user?.id || 'temp-id',
        completedSteps: new Set([...prevState.completedSteps, 1])
      }));

      // Proceed to next step
      setCurrentStep(2);

    } catch (error) {
      if (error instanceof PatientApiError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          setError(field, message);
        });
      } else {
        setError('form', error instanceof Error ? error.message : 'Registration failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [validation, updateFormData, setCurrentStep, setError, clearErrors, setSubmitting, signIn]);

  // Step 2: Profile submission
  const submitProfile = useCallback(async (formData: PatientProfileFormData): Promise<void> => {
    setSubmitting(true);
    clearErrors();

    try {
      // Validate form data - merge with existing registration data for validation
      const fullFormData = {
        ...registrationState.formData,
        ...formData
      };

      const validationResult = validation.validateStep(2, fullFormData);

      if (!validationResult.isValid) {
        setError('form', validationResult.error || 'Validation failed');
        return;
      }

      // Convert photo to base64 if provided
      let photoData = undefined;
      if (formData.photo) {
        photoData = await FileUtils.safeConvertToBase64(formData.photo);
      }

      // Submit to API
      const apiData: PatientProfileApiRequest = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        date_of_birth: formData.date_of_birth,
        address: {
          address: formData.address.street,
          city: formData.address.city,
          state: formData.address.state,
          postal_code: formData.address.postal_code,
          country: formData.address.country
        },
        phone: {
          number: formData.phone.number,
          country_code: formData.phone.country_code,
          iso_code: formData.phone.iso_code
        },
        photo: photoData,
        consent: {
          terms_of_service: true,
          privacy_policy: true,
          data_processing: true,
          biometric_processing: true,
          health_data_processing: true
        }
      };

      await apiService.user.updatePatientProfile(apiData);

      // Update state with success
      updateFormData({
        address: formData.address,
        phone: formData.phone,
        date_of_birth: formData.date_of_birth,
        photo: formData.photo
      });

      setRegistrationState(prevState => ({
        ...prevState,
        completedSteps: new Set([...prevState.completedSteps, 2])
      }));

      // Redirect to dashboard on successful profile completion
      navigate('/dashboard');

    } catch (error) {
      if (error instanceof PatientApiError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          setError(field, message);
        });
      } else {
        setError('form', error instanceof Error ? error.message : 'Profile update failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [validation, registrationState.formData, updateFormData, setError, clearErrors, setSubmitting, setCurrentStep]);

  // Reset workflow
  const resetFlow = useCallback((): void => {
    setRegistrationState({
      currentStep: 1,
      formData: {},
      isLoading: false,
      errors: {},
      isSubmitting: false,
      completedSteps: new Set()
    });
  }, []);

  // Validation helpers
  const validateStep = useCallback((step: number): boolean => {
    const result = validation.validateStep(step, registrationState.formData);
    return result.isValid;
  }, [validation, registrationState.formData]);

  const getStepErrors = useCallback((step: number): string[] => {
    const result = validation.validateStep(step, registrationState.formData);
    if (result.isValid) {
      return [];
    }
    return [result.error || 'Validation failed'];
  }, [validation, registrationState.formData]);

  const canProceedToStep = useCallback((step: number): boolean => {
    // Can proceed if previous step is completed
    return registrationState.completedSteps.has(step - 1);
  }, [registrationState.completedSteps]);

  const isStepCompleted = useCallback((step: number): boolean => {
    return registrationState.completedSteps.has(step);
  }, [registrationState.completedSteps]);

  // Computed values
  const isFormValid = useMemo(() => {
    return validateStep(registrationState.currentStep);
  }, [validateStep, registrationState.currentStep]);

  const progressPercentage = useMemo(() => {
    return (registrationState.currentStep / 2) * 100;
  }, [registrationState.currentStep]);

  return {
    state: registrationState,
    actions: {
      setCurrentStep,
      updateFormData,
      submitRegistration,
      submitProfile,
      resetFlow,
      setError,
      clearErrors,
      setLoading
    },
    validation: {
      validateStep,
      getStepErrors,
      canProceedToStep,
      isStepCompleted,
      isFormValid
    },
    computed: {
      progressPercentage
    },
  };
};